// src/components/files/CompanyFilesManager.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Badge, Alert, Progress } from "flowbite-react";
import {
  HiOutlineUpload,
  HiOutlineTrash,
  HiOutlineDownload,
} from "react-icons/hi";
import { AiOutlineLoading } from "react-icons/ai";
import moment from "moment";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";

function humanFileSize(bytes = 0) {
  if (!bytes && bytes !== 0) return "—";
  const thresh = 1024;
  if (Math.abs(bytes) < thresh) return bytes + " B";
  const units = ["KB", "MB", "GB", "TB"];
  let u = -1;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);
  return bytes.toFixed(1) + " " + units[u];
}

export default function CompanyFilesManager({
  companyId,
  maxSizeMB = 25,
  accept = "*", // ex: "application/pdf,image/*"
  className = "",
  isActive,
}) {
  const [files, setFiles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState([]); // [{file, progress, status, error}]
  const inputRef = useRef(null);

  const MAX_SIZE_BYTES = maxSizeMB * 1024 * 1024;

  const listUrl = useMemo(
    () => `${ADMIN.COMPANY_FILES_BASE}/${companyId}/files`,
    [companyId]
  );

  const fetchList = useCallback(async () => {
    if (!companyId) return;
    setLoadingList(true);
    setError(null);
    try {
      const { data } = await axios.get(listUrl);
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.data)
        ? data.data.data
        : [];
      setFiles(arr);
    } catch (e) {
      setError(e?.message || "Erro ao listar arquivos.");
    } finally {
      setLoadingList(false);
    }
  }, [companyId, listUrl]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onPickFiles = () => inputRef.current?.click();

  function validateFile(file) {
    if (file.size > MAX_SIZE_BYTES) {
      throw new Error(`Arquivo acima de ${maxSizeMB}MB: ${file.name}`);
    }
    // Validação por tipo (se desejar):
    // if (accept !== "*" && !file.type?.match(accept.replace("*", ".*"))) { ... }
  }

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;

    const items = arr.map((f) => ({
      file: f,
      progress: 0,
      status: "pending", // pending | uploading | done | error
      error: null,
      idKey: `${f.name}-${f.size}-${f.lastModified}-${Math.random()}`,
    }));
    // validação antes
    try {
      for (const it of items) validateFile(it.file);
    } catch (e) {
      setError(e.message);
      return;
    }

    setQueue((q) => [...items, ...q]);
    await uploadSequential(items);
  };

  const uploadSequential = async (items) => {
    setUploading(true);
    for (const it of items) {
      await uploadSingle(it);
    }
    setUploading(false);
    // Recarrega a lista no final
    await fetchList();
  };

  const uploadSingle = async (item) => {
    setQueue((q) =>
      q.map((x) =>
        x.idKey === item.idKey ? { ...x, status: "uploading", error: null } : x
      )
    );
    try {
      const form = new FormData();
      form.append("file", item.file);
      // Se quiser mandar metadados: form.append("description", "…");

      await axios.post(`${ADMIN.COMPANY_FILES_BASE}/${companyId}/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          const total = evt.total || item.file.size || 1;
          const percent = Math.round((evt.loaded * 100) / total);
          setQueue((q) =>
            q.map((x) =>
              x.idKey === item.idKey ? { ...x, progress: percent } : x
            )
          );
        },
      });

      setQueue((q) =>
        q.map((x) =>
          x.idKey === item.idKey ? { ...x, status: "done", progress: 100 } : x
        )
      );
    } catch (e) {
      setQueue((q) =>
        q.map((x) =>
          x.idKey === item.idKey
            ? { ...x, status: "error", error: e?.message || "Falha no upload" }
            : x
        )
      );
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dt = e.dataTransfer;
    const fileList = dt.files;
    handleFiles(fileList);
    e.currentTarget.classList.remove("ring-2", "ring-indigo-500");
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("ring-2", "ring-indigo-500");
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("ring-2", "ring-indigo-500");
  };

  const deleteFile = async (file) => {
    if (!window.confirm(`Excluir "${file.filename || file.name}"?`)) return;
    try {
      await axios.delete(
        `${ADMIN.COMPANY_FILES_BASE}/${companyId}/files/${file.id}`
      );
      await fetchList();
    } catch (e) {
      setError(e?.message || "Não foi possível excluir.");
    }
  };

  const downloadFile = async (file) => {
    try {
      const { data } = await axios.get(
        `${ADMIN.COMPANY_FILES_BASE}/${companyId}/files/${file.id}`
      );
      const link = data?.url;
      if (link) {
        // abre em nova aba (ou force download criando <a download>)
        window.open(link, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("URL de download não disponível");
      }
    } catch (e) {
      setError(e?.message || "Falha ao baixar arquivo.");
    }
  };

  return (
    <div className={className}>
      <div className="mb-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              📎 Arquivos do cliente
            </h3>
            <p className="text-xs text-gray-500">
              Tamanho máximo: {maxSizeMB}MB • Tipos aceitos:{" "}
              {accept === "*" ? "qualquer" : accept}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button size="xs" onClick={onPickFiles} disabled={uploading}>
              <HiOutlineUpload className="mr-1" /> Enviar arquivos
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={fetchList}
              disabled={loadingList}
            >
              {loadingList ? (
                <>
                  <AiOutlineLoading className="mr-1 h-4 w-4 animate-spin" />
                  Atualizando…
                </>
              ) : (
                "Atualizar lista"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Dropzone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
        onClick={onPickFiles}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <p className="text-sm text-gray-700">
          Arraste e solte arquivos aqui, ou{" "}
          <span className="font-semibold">clique para selecionar</span>.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Você pode enviar vários arquivos ao mesmo tempo.
        </p>
      </div>

      {/* Fila de upload */}
      {queue.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Uploads em andamento
          </h4>
          <ul className="space-y-2">
            {queue.map((it) => (
              <li
                key={it.idKey}
                className="rounded-lg border border-gray-200 p-3 bg-white"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {it.file.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {humanFileSize(it.file.size)} •{" "}
                      {it.file.type || "tipo desconhecido"}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 shrink-0">
                    {it.status === "uploading" && (
                      <Badge color="info" size="xs">
                        Enviando
                      </Badge>
                    )}
                    {it.status === "done" && (
                      <Badge color="success" size="xs">
                        Concluído
                      </Badge>
                    )}
                    {it.status === "error" && (
                      <Badge color="failure" size="xs">
                        Erro
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <Progress progress={it.progress} size="sm" />
                </div>
                {it.error && (
                  <div className="mt-2">
                    <Alert color="failure">{String(it.error)}</Alert>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Lista de arquivos */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          Arquivos enviados
        </h4>
        {!files?.length ? (
          <Alert color="gray">Nenhum arquivo enviado ainda.</Alert>
        ) : (
          <ul className="space-y-2">
            {files.map((f) => (
              <li
                key={f.id ?? f.objectKey ?? f.filename}
                className="rounded-lg border border-gray-200 p-3 bg-white"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {f.filename || f.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {humanFileSize(f.size)} • {f.contentType || "—"} • Enviado
                      em {moment(f.createdAt).format("DD/MM/YYYY [às] HH:mm")}
                      {f.uploader?.name ? ` • por ${f.uploader?.name}` : ""}
                    </div>
                    {f.description && (
                      <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {f.description}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => downloadFile(f)}
                    >
                      <HiOutlineDownload className="mr-1" /> Baixar
                    </Button>
                    <Button
                      size="xs"
                      color="failure"
                      onClick={() => deleteFile(f)}
                    >
                      <HiOutlineTrash className="mr-1" /> Excluir
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <div className="mt-4">
          <Alert color="failure">Erro: {String(error)}</Alert>
        </div>
      )}
    </div>
  );
}
